import { useState } from "react";
import { getTodoRole, UserRole } from "shared/types/roles";

export type TodoFilterStatus = "incomplete" | "completed" | "futureAlarms";

export type TodoFilterState = {
  roles: UserRole[];
  vehicleSetTypes: string[];
  todoStatus: TodoFilterStatus[];
};

export const defaultTodoFilterState: TodoFilterState = {
  roles: [],
  vehicleSetTypes: [],
  todoStatus: ["incomplete"],
};

export function isDefault(
  todoFilterState: TodoFilterState | null,
  currentRole: UserRole,
): boolean {
  return (
    JSON.stringify(todoFilterState) ===
    JSON.stringify({
      ...defaultTodoFilterState,
      roles: getTodoRole(currentRole),
    })
  );
}

// Uncomment "Vis åpne" if the users want to filter out opened messages as well.
// It is currently hidden to reduce complexity for users.
export const FILTER_STATUS_LIST: TodoFilterStatus[] = [
  // "incomplete",
  "completed",
];

export const VEHICLE_SET_TYPES = [
  { text: "69", value: "69" },
  { text: "70", value: "70" },
  { text: "72", value: "72" },
  { text: "73", value: "73" },
  { text: "74", value: "74" },
  { text: "75", value: "75" },
  { text: "Lok og vogn", value: "lokvogn" },
];

// add if not in list, remove if in list
export function addOrRemove<T>(list: T[], item: T): T[] {
  return list.includes(item) ? list.filter((i) => i !== item) : [...list, item];
}

// TODO Fjernes når alle er over på nye roller
const getDeprecatedExtraRole = (role: UserRole) => {
  if (
    role === UserRole.trafikkplanlegger60 ||
    role === UserRole.trafikkplanlegger63
  ) {
    return UserRole.trafikkplanlegger;
  }
  return null;
};

const getUpdatedLoklederRoles = (prev: UserRole[]) => {
  const loklederRoles = [UserRole.lokleder1, UserRole.lokleder2];
  const hasBothLoklederRoles = loklederRoles.every((role) =>
    prev.includes(role),
  );

  return hasBothLoklederRoles
    ? prev.filter((role) => !loklederRoles.includes(role))
    : [...new Set([...prev, ...loklederRoles])];
};

// TODO Fjernes når alle er over på nye roller
const addOrRemoveMultiple = (prev: UserRole[], role: UserRole) => {
  if (role === UserRole.lokleder1) {
    return getUpdatedLoklederRoles(prev);
  }

  const updatedList = addOrRemove(prev, role);
  const deprecatedRole = getDeprecatedExtraRole(role);
  switch (deprecatedRole) {
    case UserRole.trafikkplanlegger:
      return updatedList.includes(UserRole.trafikkplanlegger60) ||
        updatedList.includes(UserRole.trafikkplanlegger63)
        ? [...updatedList, UserRole.trafikkplanlegger]
        : updatedList.filter((i) => i !== UserRole.trafikkplanlegger);
    case null:
      return updatedList;
  }
};

const useTodoFilterState = (initialTodoFilterState?: TodoFilterState) => {
  const [todoFilterState, setTodoFilterState] = useState<TodoFilterState>(
    initialTodoFilterState ?? defaultTodoFilterState,
  );

  const toggleRoles = (role: UserRole) => {
    setTodoFilterState((prevState) => ({
      ...prevState,
      roles: addOrRemoveMultiple(prevState.roles, role),
    }));
  };

  const toggleVehicleSetTypes = (vehicleSetType: string) => {
    setTodoFilterState((prevState) => ({
      ...prevState,
      vehicleSetTypes: addOrRemove(prevState.vehicleSetTypes, vehicleSetType),
    }));
  };

  const toggleTodoStatus = (status: TodoFilterStatus) => {
    setTodoFilterState((prevState) => ({
      ...prevState,
      todoStatus: addOrRemove(prevState.todoStatus, status),
    }));
  };

  const clearTodoFilterState = () => {
    setTodoFilterState(defaultTodoFilterState);
  };

  return {
    todoFilterState,
    setTodoFilterState,
    clearTodoFilterState,
    toggleRoles,
    toggleVehicleSetTypes,
    toggleTodoStatus,
  };
};

export default useTodoFilterState;
